import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import {
  CONTENT_SENSITIVITY,
  getContentSensitivityLabelDescription,
  normalizeContentSensitivityReason,
} from 'src/data/generation';
import { CHANNEL } from 'src/data/channel';
import { getAssetChannel } from 'src/data/asset';
import { getGoogleContentValidationDetailsPerReason } from './googleSensitiveInfoConverter';
import { useStyles } from './useStyles';

// see https://docs.google.com/spreadsheets/d/1CcHV33CFfh_SIXplGOXSju_Q7suXU6m-uKt_c7RlzQI/edit?usp=sharing
function isGPT3Violation(reason) {
  return /The content of this variation may be (sensitive|unsafe)/i.test(reason);
}

const getTitle = (policyTitle, isFBViolation, isGoogleViolation) => {
  if (policyTitle) {
    return isGoogleViolation
      ? 'Google policy'
      : isFBViolation
      ? 'Facebook policy'
      : 'Channel policy';
  }
  return 'Content safety';
};

export const ContentSensitivityInfo = ({
  className,
  assetType,
  sensitivity,
  loading,
  horizontalLayout,
  smallerFont,
  policyTitle,
}) => {
  const styles = useStyles();
  const label = sensitivity && sensitivity.label ? sensitivity.label : '';
  const reason = sensitivity && sensitivity.reason ? sensitivity.reason : null;
  let reasonDetails = sensitivity && sensitivity.reasonDetails ? sensitivity.reasonDetails : null;
  const labelDesc = getContentSensitivityLabelDescription(label || '');
  const normalReason = normalizeContentSensitivityReason(reason || '');

  const channel = getAssetChannel(assetType);
  const isFBViolation =
    labelDesc !== CONTENT_SENSITIVITY.safe &&
    !isGPT3Violation(normalReason) &&
    channel === CHANNEL.facebook;
  const isGoogleViolation =
    labelDesc !== CONTENT_SENSITIVITY.safe &&
    !isGPT3Violation(normalReason) &&
    channel === CHANNEL.google;

  const title = getTitle(policyTitle, isFBViolation, isGoogleViolation);
  let reasonTitle = ''; // default when neither isGoogleViolation nor isFBViolation
  let learnMoreLink = '';
  let reasonDesc = normalReason;

  if (isGoogleViolation) {
    const googleDetails = getGoogleContentValidationDetailsPerReason(sensitivity);
    // eslint-disable-next-line prefer-destructuring
    reasonTitle = googleDetails.reasonTitle;
    reasonDesc = googleDetails.reasonDescription;
    // eslint-disable-next-line prefer-destructuring
    reasonDetails = googleDetails.reasonDetails;
    // eslint-disable-next-line prefer-destructuring
    learnMoreLink = googleDetails.learnMoreLink;
  } else if (isFBViolation) {
    reasonTitle = 'Possible Facebook policy violation:\n';
  }

  return (
    <div
      className={cx(styles.root, className, {
        [styles.horizontalLayout]: horizontalLayout,
        [styles.smallerFont]: smallerFont,
      })}
      data-sensitivity={labelDesc}
    >
      <div className={styles.header}>
        <Typography className={styles.title}>{title || 'Content safety'}</Typography>

        {loading ? (
          <Skeleton
            variant="rect"
            width="70px"
            height="18px"
          />
        ) : (
          <Typography className={styles.badge}>{labelDesc}</Typography>
        )}
      </div>

      <div className={styles.reason}>
        {loading ? (
          <Skeleton
            variant="text"
            width="100%"
          />
        ) : (
          <Fragment>
            <Typography className={styles.reasonTitle}>
              {reasonTitle}
              {reasonDesc}
            </Typography>

            {reasonDetails && (
              <Typography className={styles.reasonDescription}>
                {reasonDetails} {learnMoreLink}
              </Typography>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

ContentSensitivityInfo.propTypes = {
  className: PropTypes.string,
  assetType: PropTypes.string,
  sensitivity: PropTypes.shape({
    label: PropTypes.string,
    reason: PropTypes.string,
    reasonDetails: PropTypes.string,
  }),
  loading: PropTypes.bool,
  horizontalLayout: PropTypes.bool,
  smallerFont: PropTypes.bool,
  policyTitle: PropTypes.bool,
};
ContentSensitivityInfo.defaultProps = {
  className: null,
  assetType: null,
  sensitivity: null,
  loading: false,
  horizontalLayout: false,
  smallerFont: false,
  policyTitle: false,
};
